import { CSSProperties, ReactNode, useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';

import Button from '@r1-frontend/ui-react/components/buttons/button';
import { Close } from '@r1-frontend/ui-react/components/svg/main';
import { useKeyPress } from '@r1-frontend/shared/hooks/useKeyPress';
import { useRootElement } from '@r1-frontend/shared/hooks/useRootElement';
import { useScrollLock } from '@r1-frontend/shared/hooks/useScrollLock';

import * as ST from './styled';

export interface IOverlayProps {
    isOpen: boolean,
    children?: ReactNode | ReactNode[],
    align?: CSSProperties['alignItems'],
    justify?: CSSProperties['justifyContent'],
    fullWidth?: boolean,
    fullHeight?: boolean,
    onClose: () => void,
    isCloseDisabled?: boolean,
    $opacity?: CSSProperties['opacity'],
    $zIndex?: CSSProperties['zIndex'],
}

export interface IOverlayWithCloseButtonProps extends IOverlayProps {
    closeButton?: ReactNode,
}

export const Overlay = ({
    isOpen,
    children,
    align = 'center',
    justify = 'center',
    fullWidth,
    fullHeight,
    onClose,
    isCloseDisabled = false,
    $zIndex = 1000,
    $opacity = 0.5,
}: IOverlayProps): React.ReactPortal | null => {
    const overlayRef = useRef<HTMLDivElement | null>(null);
    const overlayRoot = useRootElement({ id: 'overlay-portal' });
    const { lockScroll, unlockScroll } = useScrollLock();

    /* Обеспечивает разблокировку прокрутки при закрытии попапа, при размонтировании компонента. */
    useEffect(() => {
        return () => unlockScroll();
    }, []);

    useEffect(() => {
        isOpen ? lockScroll() : unlockScroll();
    }, [isOpen]);

    const closeOverlayOnClickOutside: React.MouseEventHandler<HTMLDivElement> = useCallback(
        (event) => event.target === overlayRef.current && !isCloseDisabled && onClose(),
        [isCloseDisabled, onClose],
    );

    useKeyPress('Escape', () => !isCloseDisabled && onClose());

    const overlay = useMemo(
        () => isOpen
            ? (
                <ST.Overlay
                    data-test={'overlay'}
                    ref={overlayRef}
                    onClick={closeOverlayOnClickOutside}
                    align={align}
                    justify={justify}
                    $zIndex={$zIndex}
                    $opacity={$opacity}
                >
                    <ST.OverlayContent
                        data-test={'overlay-content'}
                        fullWidth={fullWidth}
                        fullHeight={fullHeight}
                    >
                        {children}
                    </ST.OverlayContent>
                </ST.Overlay>
            )
            : null,
        [isOpen, children, closeOverlayOnClickOutside, align, fullWidth, justify, fullHeight],
    );

    return overlayRoot ? createPortal(overlay, overlayRoot) : null;
};

export const OverlayWithCloseButton = ({
    closeButton,
    children,
    onClose,
    ...props
}: IOverlayWithCloseButtonProps) => {
    return (
        <Overlay {...props} onClose={onClose}>
            {closeButton || (
                <Button
                    bType={'secondary'}
                    size={'small'}
                    rounded
                    Icon={<Close />}
                    onClick={onClose}
                    style={{
                        position: 'absolute',
                        top: '40px',
                        right: '40px',
                    }}
                />
            )}
            {children}
        </Overlay>
    );
};
